import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';

interface UsageRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: () => void;
  isSending?: boolean;
  /** The member already has a request waiting: say so, and offer to send it again. */
  isPending?: boolean;
  /** Still asking whether a request is already waiting: placeholders for the text and the button, and no sending yet. */
  isLoading?: boolean;
}

/** Lets a member ask the organization admin for more credit or a bigger plan. */
export const UsageRequestDialog = ({
  open,
  onClose,
  onSend,
  isSending = false,
  isPending = false,
  isLoading = false,
}: UsageRequestDialogProps) => {
  const { t } = useTranslation();
  const description = isPending
    ? t('USAGE_REQUEST_PENDING', 'You already have a pending request. Want to send it again?')
    : t(
        'USAGE_REQUEST_DESCRIPTION',
        'Request your admin for additional credit or upgrade your plan'
      );
  const sendLabel = isPending ? t('SEND_AGAIN', 'Send again') : t('SEND_REQUEST', 'Send Request');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('USAGE_REQUEST', 'Usage Request')}</DialogTitle>
          <DialogDescription aria-busy={isLoading} aria-live="polite">
            {isLoading ? (
              <span className="block space-y-2 py-1" data-testid="usage-request-loading">
                <span className="block h-4 w-full animate-pulse rounded bg-muted" />
                <span className="block h-4 w-2/3 animate-pulse rounded bg-muted" />
              </span>
            ) : (
              description
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="px-6" onClick={onClose}>
            {t('CANCEL', 'Cancel')}
          </Button>
          <Button className="px-6" onClick={onSend} disabled={isSending || isLoading}>
            {isLoading ? (
              <span className="block h-4 w-20 animate-pulse rounded bg-current opacity-20" />
            ) : (
              sendLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
